import { motion } from 'framer-motion'
import { MapPin, Phone, ScrollText } from 'lucide-react'
import type { ReactNode } from 'react'
import { MOTION, outgoing } from '@/Components/ui/morph-card'
import useLocalizedValue from '@/hooks/use-localized-value'
import { useSafeTranslation } from '@/hooks/use-safe-translation'
import { cn } from '@/lib/utils'
import { branchFor, type Partner } from '@/lib/vip-card'
import { CardArrow } from './card-arrow'
import { Divider } from './divider'

export interface DetailRow {
  key: string
  icon: ReactNode
  label: string
  value: ReactNode
  top?: boolean
}

export interface PartnerFacts {
  name: string
  category: string
  place: string
  badge: string
  logo: string | null
  isLogo: boolean
  rows: DetailRow[]
}

/** Everything a card or its panel shows, resolved once and synchronously. */
export function usePartnerFacts(
  partner: Partner,
  provinceCode: number | string | null = null
): PartnerFacts {
  const t = useSafeTranslation()
  const branch = branchFor(partner, provinceCode)

  const name = useLocalizedValue(partner.name) ?? ''
  const category = useLocalizedValue(partner.type?.name) ?? ''
  const address = useLocalizedValue(branch?.address)
  const district = useLocalizedValue(branch?.district?.name)
  const province = useLocalizedValue(branch?.province?.name)
  const conditions = useLocalizedValue(partner.conditions)

  const contact = branch?.contact || partner.contact
  const fullAddress = [address, district, province].filter(Boolean).join(', ')
  const lines = (conditions ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  const rows: DetailRow[] = []

  if (fullAddress) {
    rows.push({
      key: 'address',
      icon: <MapPin />,
      label: t('vipCard.fullAddress'),
      value: fullAddress,
    })
  }

  if (contact) {
    rows.push({
      key: 'phone',
      icon: <Phone />,
      label: t('vipCard.phone'),
      value: (
        <a href={`tel:${contact}`} dir="ltr" className="text-primary hover:underline">
          {contact}
        </a>
      ),
    })
  }

  if (lines.length) {
    rows.push({
      key: 'conditions',
      icon: <ScrollText />,
      label: t('vipCard.conditions'),
      top: true,
      value: (
        <ul className="list-disc space-y-1 ps-4">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ),
    })
  }

  return {
    name,
    category,
    place: [province, district].filter(Boolean).join(', '),
    badge: t('vipCard.percentOff', { count: partner.discount_percentage }),
    logo: partner.icon,
    isLogo: partner.is_logo,
    rows,
  }
}

export function Logo({ facts, open = false }: { facts: PartnerFacts; open?: boolean }) {
  return facts.logo ? (
    <img
      src={facts.logo}
      alt=""
      className={cn('size-full', facts.isLogo ? 'object-cover' : 'object-contain')}
      draggable={false}
    />
  ) : (
    <span className={cn('rounded-full bg-muted-foreground/20', open ? 'size-5' : 'size-4')} />
  )
}

export const LOGO_BOX = 'flex shrink-0 items-center justify-center overflow-hidden bg-muted'
export const NAME = 'block truncate text-xl font-semibold leading-7 text-foreground'
export const CATEGORY = 'block truncate text-xs leading-5 text-muted-foreground'
export const BADGE =
  'flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-primary/[0.06] px-2 text-xs font-semibold leading-4 text-primary dark:bg-primary/[0.22]'

export function BadgeContent({ facts }: { facts: PartnerFacts }) {
  return (
    <>
      <img src="/images/vip-card/discount.svg" alt="" width={10} height={10} />
      {facts.badge}
    </>
  )
}

/** The line and the arrow only the resting card carries. */
export function Foot({ facts }: { facts: PartnerFacts }) {
  return (
    <>
      <span className="flex min-w-0 items-center gap-2 text-xs leading-5 text-muted-foreground">
        <img src="/images/vip-card/pin.svg" alt="" width={16} height={16} className="shrink-0" />
        <span className="truncate">{facts.place}</span>
      </span>

      <CardArrow />
    </>
  )
}

/**
 * One detail row. The icon's tile is a layer of its own, so a variant can take
 * the surfaces away before the content that sits on them.
 */
export function Row({
  row,
  divided,
  tile,
}: {
  row: DetailRow
  divided: boolean
  tile?: ReactNode
}) {
  return (
    <>
      {divided ? <Divider /> : null}

      <div className={cn('flex gap-3 px-5 py-3.5', row.top ? 'items-start' : 'items-center')}>
        <span className="relative flex size-8 shrink-0 items-center justify-center text-muted-foreground [&>svg]:relative [&>svg]:size-4">
          {tile ?? <span aria-hidden className="absolute inset-0 rounded-[8px] bg-card" />}
          {row.icon}
        </span>

        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-xs leading-4 text-muted-foreground">{row.label}</span>
          <span className="text-sm font-medium leading-5 text-foreground">{row.value}</span>
        </span>
      </div>
    </>
  )
}

/**
 * The detail rows.
 *
 * They arrive once the card's own contents have gone: the surface first, then
 * the rows a step apart. They leave together, with no order to it -- the
 * surface a little ahead of what is drawn on it, since the two fading at once
 * cross at different contrasts and read as a flicker.
 */
export function DetailRows({
  facts,
  shown,
  duration,
}: {
  facts: PartnerFacts
  shown: boolean
  /** The flight open; the flight shut is derived from it. */
  duration: number
}) {
  const leave = duration * MOTION.exit

  /* Per property, since a delay beside them is not handed down to either. */
  const arrives = (index: number) => outgoing(duration) + duration * (0.05 + index * 0.06)

  const surface = shown
    ? {
        opacity: 1,
        transition: { duration: duration * 0.45, delay: outgoing(duration), ease: 'linear' },
      }
    : { opacity: 0, transition: { duration: leave * 0.18, ease: 'linear' } }

  return (
    <div className="relative flex flex-col py-2">
      <motion.span
        aria-hidden
        data-rows-surface
        initial={{ opacity: 0 }}
        animate={surface}
        className="absolute inset-0 rounded-[16px] bg-background"
      />

      {facts.rows.map((row, index) => (
        <motion.div
          key={row.key}
          data-row
          className="relative"
          initial={{ opacity: 0, y: 12 }}
          animate={
            shown
              ? {
                  opacity: 1,
                  y: 0,
                  transition: {
                    opacity: { duration: duration * 0.4, delay: arrives(index), ease: 'linear' },
                    y: {
                      duration: duration * 0.5,
                      delay: arrives(index),
                      ease: MOTION.ease.decelerate,
                    },
                  },
                }
              : {
                  opacity: 0,
                  y: 0,
                  transition: { duration: outgoing(leave), ease: 'linear' },
                }
          }
        >
          <Row
            row={row}
            divided={index > 0}
            tile={
              <motion.span
                aria-hidden
                initial={{ opacity: 0 }}
                animate={surface}
                className="absolute inset-0 rounded-[8px] bg-card"
              />
            }
          />
        </motion.div>
      ))}
    </div>
  )
}
